import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import { useData } from '../hooks/useData';
import { TABLES } from '../constants/supabaseConstants';
import toast from 'react-hot-toast';
import { GlobalLoadingOverlay } from '../utils/GlobalLoadingOverlay';

const AppContext = createContext();

// Internal component to handle project loading
const ProjectLoader = () => {
  const { setProjects, selectProject } = useApp();
  const session = useSession();

  const { data: userProjects } = useData(TABLES.PROJECT_INFO, {
    enabled: !!session?.user?.id,
  });

  useEffect(() => {
    if (userProjects) {
      setProjects(userProjects);
      const lastProjectId = localStorage.getItem('lastSelectedProject');
      const projectToSelect = lastProjectId
        ? userProjects.find(p => p.id === lastProjectId)
        : userProjects[0];

      if (projectToSelect) {
        selectProject(projectToSelect.id);
      }
    }
  }, [userProjects, setProjects, selectProject]);

  return null;
};

// Internal component to handle user profile loading
const UserProfileLoader = () => {
  const { setUserProfile } = useApp();
  const session = useSession();

  const { data: profile } = useData(TABLES.USER_PROFILES, {
    enabled: !!session?.user?.id,
    filter: { id: session?.user?.id },
    realtime: true,
    select: 'id,full_name,email_address,role_id,status',
    single: true,
  });

  useEffect(() => {
    if (profile) {
      setUserProfile(profile);
    }
  }, [profile, setUserProfile]);

  return null;
};

export const AppProvider = ({ children }) => {
  const supabase = useSupabaseClient();
  const session = useSession();
  const [currentProject, setCurrentProject] = useState(null);
  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState({ isOpen: false, type: null, data: null });
  const [signupAttempts, setSignupAttempts] = useState(0);
  const [userProfile, setUserProfile] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [componentLoading, setComponentLoading] = useState({});
  const MAX_SIGNUP_ATTEMPTS = 1; // Strict limit - only one attempt to avoid email rate limit

  const selectProject = useCallback(
    projectId => {
      const project = projects?.find(p => p.id === projectId);
      if (project) {
        setCurrentProject(project);
        // Store in localStorage for persistence
        localStorage.setItem('lastSelectedProject', projectId);
      }
    },
    [projects],
  );

  // Add a new project to state and select it immediately
  const addAndSelectProject = useCallback(projectData => {
    setProjects(prev => [...prev, projectData]);
    setCurrentProject(projectData);
    localStorage.setItem('lastSelectedProject', projectData.id);
  }, []);

  // Restore last selected project on mount
  useEffect(() => {
    if (projects?.length > 0) {
      const lastProjectId = localStorage.getItem('lastSelectedProject');
      if (lastProjectId) {
        selectProject(lastProjectId);
      }
    }
  }, [projects, selectProject]);

  const signIn = useCallback(
    async (email, password) => {
      try {
        setLoading(true);
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        return data;
      } catch (error) {
        console.error('Login error:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [supabase],
  );

  const signup = useCallback(
    async (email, password, fullName = null, phone = null) => {
      try {
        // Check attempt limit
        if (signupAttempts >= MAX_SIGNUP_ATTEMPTS) {
          throw new Error('Maximum signup attempts reached. Please try again later.');
        }
        setSignupAttempts(prev => prev + 1);

        setLoading(true);

        // Test Supabase connection first
        try {
          // Log URL and key type (safely)
          const { error } = await supabase.from('user_profiles').select('count').limit(1);
          if (error) {
            console.error('Supabase connection test failed:', error);
            throw new Error('Could not connect to Supabase');
          }
        } catch (testError) {
          console.error('Connection test error:', testError);
          throw new Error('Could not connect to Supabase');
        }

        // First, create the auth user
        console.log('Attempting to create auth user with:', { email });
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              phone: phone,
            },
            // Add email redirect
            emailRedirectTo: window.location.origin + '/auth/callback',
          },
        });

        console.log('Auth signup response:', {
          success: !!authData?.user,
          userId: authData?.user?.id,
          confirmed: !!authData?.user?.confirmed_at,
          error: authError?.message,
        });

        if (authError) {
          console.error('Auth error:', authError);
          throw authError;
        }

        // Check if user already exists
        if (authData?.user?.identities?.length === 0) {
          console.log('Email already registered');
          throw new Error('כתובת האימייל כבר רשומה במערכת');
        }

        if (authData?.user) {
          // Get the Owner role ID
          console.log('Fetching Owner role...');
          const { data: roleData, error: roleError } = await supabase
            .from('reference_roles')
            .select('id')
            .eq('name', 'Owner')
            .single();

          console.log('Role fetch result:', { roleData, error: roleError?.message });

          if (roleError) {
            console.error('Error fetching Owner role:', roleError);
            throw new Error('Failed to set user role');
          }

          // Create user profile with Owner role
          console.log('Creating user profile with data:', {
            id: authData.user.id,
            email_address: email,
            full_name: fullName,
            role_id: roleData.id,
            status: 'Active',
          });

          // Upsert: the handle_new_user trigger may have already created a row
          const { error: profileError } = await supabase.from('user_profiles').upsert(
            {
              id: authData.user.id,
              email_address: email,
              full_name: fullName,
              phone: phone,
              role_id: roleData.id,
              status: 'Active',
            },
            { onConflict: 'id' },
          );

          console.log('Profile creation result:', { error: profileError?.message });

          if (profileError) {
            console.error('Error creating user profile:', profileError);
            throw new Error('Failed to create user profile');
          }

          console.log('Signup successful, user data:', {
            id: authData.user.id,
            email: authData.user.email,
            confirmed: !!authData.user.confirmed_at,
          });
          return authData;
        }

        throw new Error('Signup failed - no user data returned');
      } catch (error) {
        console.error('Signup error:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [supabase, signupAttempts],
  );

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setCurrentProject(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  const openModal = useCallback((type, data = {}) => {
    setModalState({ isOpen: true, type, data });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({ isOpen: false, type: null, data: null });
  }, []);

  const showSuccess = useCallback(message => {
    toast.success(message, {
      duration: 3000,
      position: 'top-center',
      style: { direction: 'rtl' },
    });
  }, []);

  const showError = useCallback(message => {
    toast.error(message, {
      duration: 5000,
      position: 'top-center',
      style: { direction: 'rtl' },
    });
  }, []);

  const showGlobalLoading = useCallback(() => {
    setGlobalLoading(true);
  }, []);

  const hideGlobalLoading = useCallback(() => {
    setGlobalLoading(false);
  }, []);

  const setComponentLoadingState = useCallback((componentId, isLoading) => {
    setComponentLoading(prev => ({
      ...prev,
      [componentId]: isLoading,
    }));
  }, []);

  const isComponentLoading = useCallback(
    componentId => {
      return componentLoading[componentId] || false;
    },
    [componentLoading],
  );

  const value = {
    user: session?.user,
    currentProject,
    setCurrentProject,
    projects,
    setProjects,
    loading,
    setLoading,
    signIn,
    signup,
    logout,
    modalState,
    openModal,
    closeModal,
    selectProject,
    addAndSelectProject,
    userProfile,
    setUserProfile,
    // New notification and loading methods
    showSuccess,
    showError,
    showGlobalLoading,
    hideGlobalLoading,
    setComponentLoadingState,
    isComponentLoading,
    globalLoading,
    componentLoading,
  };

  return (
    <AppContext.Provider value={value}>
      <ProjectLoader />
      <UserProfileLoader />
      {globalLoading && <GlobalLoadingOverlay />}
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
